import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Code, 
  Copy, 
  Check, 
  ArrowLeft, 
  RotateCcw, 
  Share2, 
  Eye, 
  CheckCircle2, 
  Image as ImageIcon 
} from 'lucide-react';

export const OpenGraphGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  const [ogTitle, setOgTitle] = useState('Supercharge Your Analytics Workflow');
  const [ogDescription, setOgDescription] = useState('Unify your marketing data, automate client reporting, and boost conversion rates with our all-in-one AI platform.');
  const [ogUrl, setOgUrl] = useState('https://toolmetrico.com');
  const [ogImage, setOgImage] = useState('https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80');
  const [ogType, setOgType] = useState('website');
  const [siteName, setSiteName] = useState('Toolmetrico Suite');
  const [twitterCard, setTwitterCard] = useState('summary_large_image');
  const [platformView, setPlatformView] = useState('facebook'); // 'facebook' | 'twitter' | 'linkedin'
  const [copied, setCopied] = useState(false);

  const generatedCode = `<!-- Open Graph / Facebook Meta Tags -->
<meta property="og:title" content="${ogTitle || ''}">
<meta property="og:description" content="${ogDescription || ''}">
<meta property="og:type" content="${ogType}">
<meta property="og:url" content="${ogUrl || ''}">
<meta property="og:image" content="${ogImage || ''}">
<meta property="og:site_name" content="${siteName || ''}">

<!-- Twitter Card Meta Tags -->
<meta name="twitter:card" content="${twitterCard}">
<meta name="twitter:title" content="${ogTitle || ''}">
<meta name="twitter:description" content="${ogDescription || ''}">
<meta name="twitter:image" content="${ogImage || ''}">
<meta name="twitter:site" content="@${siteName ? siteName.toLowerCase().replace(/\s+/g, '') : 'brand'}">`;

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => {
    setOgTitle('');
    setOgDescription('');
    setOgUrl('');
    setOgImage('');
    setSiteName('');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Open Graph Generator - Social Meta Tags" 
        description="Generate Open Graph and Twitter Card meta tags. Preview social share snippets for Facebook, LinkedIn, Twitter, and Discord instantly."
        keywords="open graph generator, og tags, og image generator, twitter card generator, facebook preview, social media meta tags"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Open Graph Generator</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Open Graph & Twitter Card Generator
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Generate social share protocol tags and preview exactly how your links render when shared across Facebook, Twitter, and LinkedIn feeds.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>OG Protocol Configs</h3>
            <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RotateCcw size={14} /> Clear All
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Title */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>OG Title</label>
              <input 
                type="text" 
                value={ogTitle} 
                onChange={(e) => setOgTitle(e.target.value)}
                placeholder="E.g. Toolmetrico Pro - Developer Utilities"
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>OG Description</label>
              <textarea 
                rows="3"
                value={ogDescription} 
                onChange={(e) => setOgDescription(e.target.value)}
                placeholder="E.g. Explore our fast client-side tools..."
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>

            {/* URL */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Canonical URL</label>
              <input 
                type="text" 
                value={ogUrl} 
                onChange={(e) => setOgUrl(e.target.value)}
                placeholder="https://example.com/page"
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
            </div>

            {/* Image URL */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>OG Image URL (1200 x 630px Recommended)</label>
              <input 
                type="text" 
                value={ogImage} 
                onChange={(e) => setOgImage(e.target.value)}
                placeholder="https://example.com/cover.jpg"
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
            </div>

            {/* Site Name & Type */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Site Name</label>
                <input 
                  type="text" 
                  value={siteName} 
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="Brand Name"
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>OG Object Type</label>
                <select 
                  value={ogType} 
                  onChange={(e) => setOgType(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                >
                  <option value="website">Website</option>
                  <option value="article">Article / Blog Post</option>
                  <option value="product">Product / E-commerce</option>
                  <option value="profile">User Profile</option>
                </select>
              </div>
            </div>

            {/* Twitter Card */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Twitter Card Type</label>
              <select 
                value={twitterCard} 
                onChange={(e) => setTwitterCard(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              >
                <option value="summary_large_image">Summary with Large Image (Recommended)</option>
                <option value="summary">Summary Card (Small Thumbnail)</option>
              </select>
            </div>

          </div>
        </div>

        {/* Right Side: Output Code + Live Social Previews */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* Output Code Card */}
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Code size={18} style={{ color: 'var(--primary)' }} /> Open Graph Meta Tags
              </h3>
              <AdToast message={toast} />
              <button 
                onClick={withAd('og-tag-copy', handleCopy)}
                style={{ background: copied ? '#10b981' : 'var(--primary)', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied Code!' : 'Copy Tags'}
              </button>
            </div>

            <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: '20px', borderRadius: '14px', fontSize: '0.85rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto', margin: 0, border: '1px solid rgba(255, 255, 255, 0.1)', lineHeight: 1.6 }}>
              {generatedCode}
            </pre>
          </div>

          {/* Live Social Share Previews */}
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Share2 size={18} style={{ color: '#0284c7' }} /> Social Share Card Preview
              </h3>

              <div style={{ display: 'flex', gap: '6px', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '10px' }}>
                <button 
                  onClick={() => setPlatformView('facebook')}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', background: platformView === 'facebook' ? '#1877f2' : 'transparent', color: platformView === 'facebook' ? '#ffffff' : 'var(--text-secondary)', transition: 'var(--transition-smooth)' }}
                >
                  Facebook
                </button>
                <button 
                  onClick={() => setPlatformView('twitter')}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', background: platformView === 'twitter' ? '#000000' : 'transparent', color: platformView === 'twitter' ? '#ffffff' : 'var(--text-secondary)', transition: 'var(--transition-smooth)' }}
                >
                  X (Twitter)
                </button>
                <button 
                  onClick={() => setPlatformView('linkedin')}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', background: platformView === 'linkedin' ? '#0a66c2' : 'transparent', color: platformView === 'linkedin' ? '#ffffff' : 'var(--text-secondary)', transition: 'var(--transition-smooth)' }}
                >
                  LinkedIn
                </button>
              </div>
            </div>

            {/* Simulated Share Cards */}
            <div style={{ maxWidth: '580px', margin: '0 auto', border: '1px solid #cbd5e1', borderRadius: '12px', overflow: 'hidden', background: '#ffffff', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
              
              {/* Image Header */}
              <div style={{ width: '100%', aspectRatio: twitterCard === 'summary' && platformView === 'twitter' ? '1/1' : '16/8.4', background: '#f1f5f9', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {ogImage ? (
                  <img src={ogImage} alt="OG Cover Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => e.target.style.display = 'none'} />
                ) : (
                  <ImageIcon size={48} style={{ color: '#94a3b8' }} />
                )}
              </div>

              {/* Meta Card Details */}
              <div style={{ padding: '16px', borderTop: '1px solid #cbd5e1', background: platformView === 'facebook' ? '#f8fafc' : '#ffffff' }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', fontWeight: 700 }}>
                  {siteName || 'example.com'}
                </div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '6px', lineHeight: 1.3 }}>
                  {ogTitle || 'Website Share Title'}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.4 }}>
                  {ogDescription || 'Summary description of the shared web link appears here.'}
                </div>
              </div>

            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="open-graph-generator" />
    </div>
  );
};

export default OpenGraphGenerator;
